import React, { useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContent, Container, Spinner, Button, ButtonIcon, Row, Pagination } from 'components';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { formatDateRange } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadYcMeetingsCreator,
  setYcMeetingsPageCreator,
  setYcMeetingsSortCreator,
  setYcMeetingsFiltersCreator,
} from 'store/actionsCreators';
import { iconList } from 'assets';
import MeetingsTable, { InvoiceIcon } from 'containers/ma/Meetings/Meetings.styled';
import { flagKeys, flagColumnKeys } from 'containers/ma/Meetings/Application/flag';

/**
 * An accepted application is settled and only readable. A rejected one is not:
 * the officer sends it in again, so it keeps its edit action while the meeting
 * is open.
 */
const DECIDED = ['ACCEPTED'];

/** The list names the month a meeting is held in, not the exact day. */
const MONTH_FORMAT = 'MMMM YYYY';

/**
 * The YC Assembly meetings a Young Crew officer applies to by sending in the
 * signed confidentiality agreement. Every row is the reader's own application.
 */
const MeetingsList = ({
  meetingType,
  loading,
  list,
  loadList,
  setPage,
  setSort,
  setFilters,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();

  // Permissions arrive after the first render, and until they do there is
  // nothing to deny yet - denying early would throw out a user who is allowed.
  const permissionsKnown = !!userPermissions;
  const canView =
    !permissionsKnown ||
    checkPermissions({ roles: [`yc_meeting_view_${meetingType}`] }, userPermissions);

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    canView,
    '/yc',
    'page.yc.meetings.accessDenied',
  );

  useEffect(() => {
    if (!allowed) return;

    setPage(1);
    loadList();
  }, [sort, allowed]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const basePath = `/yc/meetings/${meetingType}`;
  // The NDA is never invoiced, so the column is the verified one throughout.
  const columnFlag = flagColumnKeys(meetingType);

  const statusOptions = [
    { value: 'DRAFT', label: t('page.ma.meetings.list.statusDraft') },
    { value: 'SUBMITTED', label: t('page.ma.meetings.list.statusSubmitted') },
    { value: 'ACCEPTED', label: t('page.ma.meetings.list.statusAccepted') },
    { value: 'REJECTED', label: t('page.ma.meetings.list.statusRejected') },
    { value: 'NONE', label: t('page.ma.meetings.list.statusNone') },
  ];

  const renderFlag = (item) => {
    if (!item.application) return <span>-</span>;

    const on = !!item.application.verified;
    const keys = flagKeys(item.application.flag);

    return (
      <InvoiceIcon paid={on} title={t(on ? keys.onLong : keys.offLong)}>
        <ReactSVG src={iconList[on ? 'checkThin' : 'close']} />
      </InvoiceIcon>
    );
  };

  const renderActions = (item) => {
    // Once IPMA accepted an application it is frozen, so the row offers the
    // read only view even while the meeting is still open.
    const decided = item.application && DECIDED.includes(item.application.status);

    if (item.canApply && !decided) {
      return item.application ? (
        <ButtonIcon
          icon="edit"
          secondary
          as={Button}
          title={t('page.ma.meetings.list.edit')}
          onClick={() => history.push(`${basePath}/application/${item.application.id}/edit`)}
        />
      ) : (
        <Button
          style={{ width: 'auto' }}
          secondary
          onClick={() => history.push(`${basePath}/application/create/${item.id}`)}
        >
          {t('page.ma.meetings.list.create')}
        </Button>
      );
    }

    if (item.application) {
      return (
        <ButtonIcon
          icon="eye"
          secondary
          as={Button}
          title={t('page.ma.meetings.list.view')}
          onClick={() => history.push(`${basePath}/application/${item.application.id}/view`)}
        />
      );
    }

    return <span>-</span>;
  };

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <PageContent>
      <Container>
        <MeetingsTable tablebreakpoint="lg" actionsWidth="12rem">
          <MeetingsTable.Head>
            <MeetingsTable.Row>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.ma.meetings.list.namePlaceholder')}
                  label={t('page.ma.meetings.list.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFrom || ''}
                  filterValueTo={filters.dateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateTo', val)}
                  filterPlaceholder={t('page.ma.meetings.list.datePlaceholder')}
                  label={t('page.ma.meetings.list.date')}
                  sort
                  sortDirection={getSortDirection('date')}
                  onSort={(dir) => handleColumnSort('date', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={statusOptions}
                  filterPlaceholder={t('page.ma.meetings.list.statusPlaceholder')}
                  label={t('page.ma.meetings.list.status')}
                  sort
                  sortDirection={getSortDirection('status')}
                  onSort={(dir) => handleColumnSort('status', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t(columnFlag.column)} isAction />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.list.actions')} isAction />
              </MeetingsTable.Heading>
            </MeetingsTable.Row>
          </MeetingsTable.Head>
          <MeetingsTable.Body>
            {loading ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={5}>
                  <Spinner />
                </MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={5}>{t('global.noResultsFound')}</MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <MeetingsTable.Row key={item.id}>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t('page.ma.meetings.list.name')}:</MeetingsTable.Label>
                      {item.name}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t('page.ma.meetings.list.date')}:</MeetingsTable.Label>
                      {formatDateRange(item.dateFrom, item.dateTo, MONTH_FORMAT)}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.status')}:
                      </MeetingsTable.Label>
                      {item.application
                        ? item.application.statusLabel
                        : t('page.ma.meetings.list.statusNone')}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t(columnFlag.column)}:</MeetingsTable.Label>
                      <Row style={{ justifyContent: 'center' }}>{renderFlag(item)}</Row>
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.actions')}:
                      </MeetingsTable.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        {renderActions(item)}
                      </Row>
                    </MeetingsTable.Cell>
                  </MeetingsTable.Row>
                ))
              : null}
          </MeetingsTable.Body>
        </MeetingsTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </Container>
    </PageContent>
  );
};

MeetingsList.propTypes = {
  meetingType: PropTypes.oneOf(['ycnda']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

MeetingsList.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, yc, user }, { meetingType }) => ({
  loading: isLoading(loading, 'GLOBAL'),
  list: yc.meetings[meetingType],
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch, { meetingType }) => ({
  loadList: () => dispatch(loadYcMeetingsCreator(meetingType)),
  setPage: (payload) => dispatch(setYcMeetingsPageCreator(meetingType, payload)),
  setSort: (payload) => dispatch(setYcMeetingsSortCreator(meetingType, payload)),
  setFilters: (payload) => dispatch(setYcMeetingsFiltersCreator(meetingType, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MeetingsList);
